import { createContext, useContext, useCallback, useEffect, useState, type ReactNode } from 'react';
import apiClient from '../config/api';
import type { User } from '../types';

interface AuthContextValue {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  signIn: (googleCredential: string) => Promise<void>;
  login: (token: string, user: User) => void;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('auth_token'));
  const [isLoading, setIsLoading] = useState(true);

  const signIn = useCallback(async (googleCredential: string) => {
    try {
      const response = await apiClient.post('/api/auth/google-signin', {
        credential: googleCredential,
      });
      
      const { token: newToken, user: userData, refresh_token } = response.data;
      
      // Store tokens
      localStorage.setItem('auth_token', newToken);
      if (refresh_token) {
        localStorage.setItem('refresh_token', refresh_token);
      }
      if (userData.email) {
        localStorage.setItem('user_email', userData.email);
      }
      
      setToken(newToken);
      setUser({
        userId: userData.user_id || userData.userId,
        email: userData.email,
        fullName: userData.full_name || userData.fullName,
        role: userData.role,
        isActive: userData.is_active !== undefined ? userData.is_active : userData.isActive,
      });
    } catch (error) {
      console.error('Sign in error:', error);
      throw error;
    }
  }, []);

  const login = useCallback((newToken: string, newUser: User) => {
    localStorage.setItem('auth_token', newToken);
    setToken(newToken);
    setUser(newUser);
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiClient.post('/api/auth/logout');
    } catch {
      // Ignore logout API errors — clear local state regardless
    } finally {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user_email');
      setToken(null);
      setUser(null);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/auth/me');
      const userData = response.data.user;
      setUser({
        userId: userData.user_id,
        email: userData.email,
        fullName: userData.full_name,
        role: userData.role,
        isActive: userData.is_active,
      });
    } catch {
      // Token invalid and refresh also failed — clear auth state
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user_email');
      setToken(null);
      setUser(null);
    }
  }, []);

  // On mount, if we have a stored token, validate it by fetching user info
  useEffect(() => {
    if (token) {
      refreshUser().finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const value: AuthContextValue = {
    user,
    token,
    isAuthenticated: !!user && !!token,
    isLoading,
    signIn,
    login,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
